import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  ComposedChart,
  Area,
  AreaChart
} from 'recharts';
import {
  Eye,
  Users,
  TrendingUp,
  MapPin,
  Clock,
  Monitor,
  RefreshCw,
  Calendar,
  Filter,
  Download,
  ToggleLeft,
  ToggleRight,
  Smartphone,
  Tablet,
  Laptop,
  Bot,
  User,
  ExternalLink,
  Globe,
  BarChart3,
  Activity
} from 'lucide-react';
import {
  getVisitorAnalytics,
  getDailyVisitorCounts,
  getTopCountries,
  getRecentVisits
} from '../utils/visitorTracking';
import WorldMap from './WorldMap';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316'];

const VisitorAnalytics = ({ token }) => {
  const [analytics, setAnalytics] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [countriesData, setCountriesData] = useState([]);
  const [recentVisits, setRecentVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return document.documentElement.classList.contains('dark');
  });
  
  // New state for enhanced features
  const [excludeBots, setExcludeBots] = useState(true);
  const [selectedDateRange, setSelectedDateRange] = useState('today');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [activeTab, setActiveTab] = useState('browsers');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [filters, setFilters] = useState({
    country: '',
    path: '',
    referrer: '',
    device: ''
  });
  const [showHeatmap, setShowHeatmap] = useState(false);
  
  // Fixed time period analytics for cards
  const [fixedPeriodAnalytics, setFixedPeriodAnalytics] = useState({
    today: null,
    yesterday: null,
    sevenDays: null,
    thirtyDays: null,
    ninetyDays: null,
    allTime: null
  });

  const getDateRangeParams = () => {
    const now = new Date();
    let startDate, endDate;
    

    
    if (selectedDateRange === 'custom') {
      if (customDateRange.startDate && customDateRange.endDate) {
        startDate = customDateRange.startDate;
        endDate = customDateRange.endDate;
      } else {
        // Default to today if custom dates not set
        startDate = now.toISOString().split('T')[0];
        endDate = now.toISOString().split('T')[0];
      }
    } else if (selectedDateRange === 'today') {
      startDate = now.toISOString().split('T')[0];
      endDate = now.toISOString().split('T')[0];

    } else if (selectedDateRange === 'yesterday') {
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      startDate = yesterday.toISOString().split('T')[0];
      endDate = yesterday.toISOString().split('T')[0];
    } else if (selectedDateRange === 'all') {
      // For "all" data, don't send date parameters to get all historical data
      return null;
    } else {
      const days = parseInt(selectedDateRange.replace('d', ''));
      startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      endDate = now.toISOString().split('T')[0];

    }
    
    const result = { start_date: startDate, end_date: endDate };

    return result;
  };

  const formatDateToDDMMMYYYY = (date) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate().toString().padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getDateRangeDisplayText = () => {
    const now = new Date();
    
    switch(selectedDateRange) {
      case 'today':
        return `Today (${formatDateToDDMMMYYYY(now)})`;
      case 'yesterday':
        const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        return `Yesterday (${formatDateToDDMMMYYYY(yesterday)})`;
      case 'all':
        return 'All Time';
      case 'custom':
        if (customDateRange.startDate && customDateRange.endDate) {
          const start = new Date(customDateRange.startDate);
          const end = new Date(customDateRange.endDate);
          return `${formatDateToDDMMMYYYY(start)} - ${formatDateToDDMMMYYYY(end)}`;
        }
        return 'Custom Range';
      default:
        const days = parseInt(selectedDateRange.replace('d', ''));
        const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
        return `Last ${days} days (${formatDateToDDMMMYYYY(startDate)} - ${formatDateToDDMMMYYYY(now)})`;
    }
  };

  const getFixedDateRange = (period) => {
    const now = new Date();
    let startDate, endDate;
    
    switch(period) {
      case 'today':
        startDate = now.toISOString().split('T')[0];
        endDate = now.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        startDate = yesterday.toISOString().split('T')[0];
        endDate = yesterday.toISOString().split('T')[0];
        break;
      case 'sevenDays':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        endDate = now.toISOString().split('T')[0];
        break;
      case 'thirtyDays':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        endDate = now.toISOString().split('T')[0];
        break;
      case 'ninetyDays':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        endDate = now.toISOString().split('T')[0];
        break;
      case 'allTime':
        return null; // No date range for all time
      default:
        return null;
    }
    
    return { start_date: startDate, end_date: endDate };
  };

  const fetchFixedPeriodAnalytics = async () => {
    try {
      const periods = ['today', 'yesterday', 'sevenDays', 'thirtyDays', 'ninetyDays', 'allTime'];
      const results = {};
      
      // Fetch analytics for each fixed period
      for (const period of periods) {
        const dateRange = getFixedDateRange(period);
        const analyticsRes = await getVisitorAnalytics(token, dateRange);
        results[period] = analyticsRes.data;
      }
      
      setFixedPeriodAnalytics(results);
    } catch (err) {
      console.error('Error fetching fixed period analytics:', err);
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      setError(null);
      
      const dateRange = getDateRangeParams();

      
      const days = selectedDateRange === 'custom' ? 30 : 
                    selectedDateRange === 'today' ? 1 :
                    selectedDateRange === 'yesterday' ? 1 :
                    selectedDateRange === 'all' ? 365 :
                    parseInt(selectedDateRange.replace('d', ''));
      
      // Fetch all analytics data in parallel
      const [analyticsRes, dailyRes, countriesRes, recentRes] = await Promise.all([
        getVisitorAnalytics(token, dateRange),
        getDailyVisitorCounts(token, days, dateRange),
        getTopCountries(token, 10, dateRange),
        getRecentVisits(token, 200, dateRange)
      ]);



      setAnalytics(analyticsRes.data);
      setDailyData(dailyRes.data || []);
      // Set countries data directly from API response
      setCountriesData(countriesRes.data || []);
      
      setRecentVisits(recentRes.data || []);
      
      // Also fetch fixed period analytics for cards
      await fetchFixedPeriodAnalytics();
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Enhanced data processing
  const processedData = useMemo(() => {
    if (!recentVisits.length) return {
      filteredVisits: [],
      referrerData: [],
      deviceData: [],
      browserData: [],
      osData: [],
      heatmapData: [],
      sparklineData: [],
      funnelData: [],
      dashboardPaths: []
    };

    // Get date range for filtering
    const dateRange = getDateRangeParams();
    let startDate, endDate;
    
    if (dateRange) {
      startDate = new Date(dateRange.start_date + 'T00:00:00.000Z');
      endDate = new Date(dateRange.end_date + 'T23:59:59.999Z');
    }

    // Filter visits based on date range, bot exclusion and other filters
    let filteredVisits = recentVisits.filter(visit => {
      // Date range filtering (skip if 'all' time is selected)
      if (dateRange) {
        const visitDate = new Date(visit.timestamp);
        if (visitDate < startDate || visitDate > endDate) {
          return false;
        }
      }
      
      if (excludeBots && visit.is_bot) return false;
      if (filters.country && !visit.country?.toLowerCase().includes(filters.country.toLowerCase())) return false;
      if (filters.path && !visit.path?.toLowerCase().includes(filters.path.toLowerCase())) return false;
      if (filters.referrer && !visit.referrer?.toLowerCase().includes(filters.referrer.toLowerCase())) return false;
      return true;
    });
    


    // Process referrer data - assign each unique visitor to their primary source
    const visitorSources = {}; // Map of IP -> {source, timestamp}
    
    // Helper function to process referrer
    const processReferrer = (referrer) => {
      if (!referrer || referrer === 'Direct') return 'Direct';
      
      try {
        // Check for mobile app referrers first
        if (referrer.startsWith('android-app://')) {
          if (referrer.includes('linkedin')) return 'LinkedIn';
          if (referrer.includes('facebook')) return 'Facebook';
          if (referrer.includes('twitter')) return 'Twitter';
          if (referrer.includes('instagram')) return 'Instagram';
          return 'Mobile App';
        } else if (referrer.startsWith('ios-app://')) {
          if (referrer.includes('linkedin')) return 'LinkedIn';
          if (referrer.includes('facebook')) return 'Facebook';
          if (referrer.includes('twitter')) return 'Twitter';
          if (referrer.includes('instagram')) return 'Instagram';
          return 'Mobile App';
        } else if (referrer.includes('linkedin.com') || referrer.includes('lnkd.in')) {
          return 'LinkedIn';
        } else if (referrer.includes('google.com') || referrer.includes('google.')) {
          return 'Google';
        } else if (referrer.includes('twitter.com') || referrer.includes('t.co')) {
          return 'Twitter';
        } else if (referrer.includes('facebook.com') || referrer.includes('fb.com')) {
          return 'Facebook';
        } else if (referrer.includes('instagram.com')) {
          return 'Instagram';
        } else if (referrer.includes('youtube.com')) {
          return 'YouTube';
        } else if (referrer.includes('github.com')) {
          return 'GitHub';
        } else {
          // Try to extract hostname for other domains
          try {
            const url = new URL(referrer.startsWith('http') ? referrer : `https://${referrer}`);
            return url.hostname.replace('www.', '');
          } catch {
            return 'Other';
          }
        }
      } catch (error) {
        return 'Other';
      }
    };
    
    // Process each visit and assign visitors to their most recent non-direct source
    // or Direct if they only have direct visits
    filteredVisits.forEach(visit => {
      const ip = visit.ip_address;
      const timestamp = new Date(visit.timestamp);
      const source = processReferrer(visit.referrer);
      
      if (!visitorSources[ip]) {
        visitorSources[ip] = { source, timestamp };
      } else {
        // If this visit is more recent, or if current source is Direct and new source isn't
        const currentSource = visitorSources[ip].source;
        const currentTimestamp = visitorSources[ip].timestamp;
        
        if (timestamp > currentTimestamp || 
            (currentSource === 'Direct' && source !== 'Direct')) {
          visitorSources[ip] = { source, timestamp };
        }
      }
    });
    
    // Count unique visitors by their assigned source
    const referrerUniqueVisitors = {};
    Object.values(visitorSources).forEach(({ source }) => {
      referrerUniqueVisitors[source] = (referrerUniqueVisitors[source] || 0) + 1;
    });
    
    // Get the correct total unique visitors from API data based on selected date range
    let totalUniqueVisitors = 0;
    if (selectedDateRange === 'today') {
      totalUniqueVisitors = fixedPeriodAnalytics.today?.unique_visitors_today || 0;
    } else if (selectedDateRange === 'yesterday') {
      totalUniqueVisitors = fixedPeriodAnalytics.yesterday?.unique_visitors_range || 0;
    } else if (selectedDateRange === '7d') {
      totalUniqueVisitors = fixedPeriodAnalytics.sevenDays?.unique_visitors_range || 0;
    } else if (selectedDateRange === '30d') {
      totalUniqueVisitors = fixedPeriodAnalytics.thirtyDays?.unique_visitors_range || 0;
    } else if (selectedDateRange === '90d') {
      totalUniqueVisitors = fixedPeriodAnalytics.ninetyDays?.unique_visitors_range || 0;
    } else if (selectedDateRange === 'all') {
      totalUniqueVisitors = fixedPeriodAnalytics.allTime?.unique_visitors_range || 0;
    } else {
      // For custom date ranges, fall back to calculated value
      totalUniqueVisitors = Object.keys(visitorSources).length;
    }
    
    // If API data is not available, fall back to calculated value
    if (totalUniqueVisitors === 0) {
      totalUniqueVisitors = Object.keys(visitorSources).length;
    }
    
    // Calculate scaling factor to match API total with processed data
    const processedTotal = Object.keys(visitorSources).length;
    const scalingFactor = processedTotal > 0 ? totalUniqueVisitors / processedTotal : 1;
    
    const referrerData = Object.entries(referrerUniqueVisitors)
      .map(([source, count]) => ({ 
        source, 
        count: Math.round(count * scalingFactor), // Scale the count to match API total
        percentage: totalUniqueVisitors > 0 ? (count / processedTotal * 100).toFixed(1) : '0.0'
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
      


    // Process device data
    const deviceCounts = { Mobile: 0, Desktop: 0, Tablet: 0 };
    filteredVisits.forEach(visit => {
      const ua = visit.user_agent || '';
      if (ua.includes('Mobile') || ua.includes('Android') || ua.includes('iPhone')) {
        deviceCounts.Mobile++;
      } else if (ua.includes('Tablet') || ua.includes('iPad')) {
        deviceCounts.Tablet++;
      } else {
        deviceCounts.Desktop++;
      }
    });
    
    const deviceData = Object.entries(deviceCounts)
      .map(([device, count]) => ({ device, count, percentage: (count / filteredVisits.length * 100).toFixed(1) }))
      .filter(item => item.count > 0);

    // Process browser and OS data
    const browserCounts = {};
    const osCounts = {};
    
    filteredVisits.forEach(visit => {
      const ua = visit.user_agent || '';
      
      // Extract browser
      const browserMatch = ua.match(/(Chrome|Firefox|Safari|Edge|Opera)\/([\d.]+)/);
      const browser = browserMatch ? browserMatch[1] : 'Other';
      browserCounts[browser] = (browserCounts[browser] || 0) + 1;
      
      // Extract OS
      const os = ua.includes('Windows') ? 'Windows' :
        ua.includes('Mac') ? 'macOS' :
        ua.includes('Linux') ? 'Linux' :
        ua.includes('Android') ? 'Android' :
        ua.includes('iOS') ? 'iOS' : 'Other';
      osCounts[os] = (osCounts[os] || 0) + 1;
    });
    
    const browserData = Object.entries(browserCounts)
      .map(([browser, count]) => ({ name: browser, count, percentage: (count / filteredVisits.length * 100).toFixed(1) }))
      .sort((a, b) => b.count - a.count);
      
    const osData = Object.entries(osCounts)
      .map(([os, count]) => ({ name: os, count, percentage: (count / filteredVisits.length * 100).toFixed(1) }))
      .sort((a, b) => b.count - a.count);

    // Process heatmap data (day of week vs hour)
    const heatmapData = [];
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    for (let day = 0; day < 7; day++) {
      for (let hour = 0; hour < 24; hour++) {
        const count = filteredVisits.filter(visit => {
          const date = new Date(visit.timestamp);
          return date.getDay() === day && date.getHours() === hour;
        }).length;
        
        heatmapData.push({
          day: days[day],
          hour,
          count,
          dayIndex: day
        });
      }
    }

    // Generate sparkline data for last 7 days
    const sparklineData = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dayStart = new Date(date.setHours(0, 0, 0, 0));
      const dayEnd = new Date(date.setHours(23, 59, 59, 999));
      
      const count = filteredVisits.filter(visit => {
        const visitDate = new Date(visit.timestamp);
        return visitDate >= dayStart && visitDate <= dayEnd;
      }).length;
      
      sparklineData.push({ date: dayStart.toISOString().split('T')[0], count });
    }

    // Simple funnel analysis
    const pathCounts = {};
    filteredVisits.forEach(visit => {
      const path = visit.path || '/';
      pathCounts[path] = (pathCounts[path] || 0) + 1;
    });
    
    const funnelData = [
      { step: 'Homepage', path: '/', count: pathCounts['/'] || 0 },
      { step: 'Gallery', path: '/gallery', count: pathCounts['/gallery'] || 0 },
      { step: 'Contact', path: '/contact', count: pathCounts['/contact'] || 0 }
    ];

    // Find most visited dashboard pages (exclude development /dashboard path)
    const dashboardPaths = Object.entries(pathCounts)
      .filter(([path]) => path.startsWith('/dashboard') && path !== '/dashboard')
      .map(([path, count]) => ({ path, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 4); // Top 4 dashboard pages
    


    // Process top countries data
    const countryCounts = {};
    filteredVisits.forEach(visit => {
      const country = visit.country || 'Unknown';
      countryCounts[country] = (countryCounts[country] || 0) + 1;
    });
    
    const topCountries = Object.entries(countryCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);

    // Sort visits by timestamp in descending order (newest first)
    const sortedFilteredVisits = filteredVisits.sort((a, b) => {
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);
      return dateB - dateA; // Descending order
    });

    return {
      filteredVisits: sortedFilteredVisits,
      referrerData,
      deviceData,
      browserData,
      osData,
      heatmapData,
      sparklineData,
      funnelData,
      topCountries,
      dashboardPaths
    };
  }, [recentVisits, excludeBots, filters, selectedDateRange, customDateRange]);

  useEffect(() => {
    if (token) {
      fetchAnalyticsData();
    }
  }, [token, selectedDateRange, customDateRange]);

  // Fetch fixed period analytics for cards
  useEffect(() => {
    if (token) {
      fetchFixedPeriodAnalytics();
    }
  }, [token]);

  // Monitor dark mode changes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          setIsDarkMode(document.documentElement.classList.contains('dark'));
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalyticsData();
  };

  // Utility functions
  const exportToCSV = () => {
    const headers = ['Path', 'Country', 'City', 'Browser/OS', 'Timestamp', 'Referrer', 'Is Bot'];
    const csvData = (processedData.filteredVisits || []).map(visit => [
      visit.path || '/',
      visit.country || 'Unknown',
      visit.city || 'Unknown',
      formatUserAgent(visit.user_agent),
      formatDateTime(visit.timestamp),
      visit.referrer || 'Direct',
      visit.is_bot ? 'Yes' : 'No'
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `visitor-analytics-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ country: '', path: '', referrer: '', device: '' });
  };

  const getDeviceIcon = (device) => {
    switch (device) {
      case 'Mobile': return <Smartphone className="h-4 w-4" />;
      case 'Tablet': return <Tablet className="h-4 w-4" />;
      case 'Desktop': return <Laptop className="h-4 w-4" />;
      default: return <Monitor className="h-4 w-4" />;
    }
  };

  const getHeatmapColor = (count, maxCount) => {
    if (count === 0) return 'bg-gray-100 dark:bg-gray-800';
    const intensity = count / maxCount;
    if (intensity > 0.8) return 'bg-blue-600';
    if (intensity > 0.6) return 'bg-blue-500';
    if (intensity > 0.4) return 'bg-blue-400';
    if (intensity > 0.2) return 'bg-blue-300';
    return 'bg-blue-200';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Invalid Date';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error, 'for value:', dateString);
      return 'Invalid Date';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) {
      console.log('formatDateTime: Empty dateString received');
      return 'Invalid Date';
    }
    
    try {
      let date;
      
      // Handle different timestamp formats
      if (typeof dateString === 'number') {
        // Unix timestamp (seconds or milliseconds)
        date = new Date(dateString > 1000000000000 ? dateString : dateString * 1000);
      } else if (typeof dateString === 'string') {
        // Handle various string formats
        if (dateString.includes('T') || dateString.includes('Z')) {
          // ISO format
          date = new Date(dateString);
        } else if (dateString.match(/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/)) {
          // SQL datetime format: YYYY-MM-DD HH:MM:SS
          date = new Date(dateString.replace(' ', 'T') + 'Z');
        } else {
          // Try parsing as-is
          date = new Date(dateString);
        }
      } else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      const formatted = date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      return formatted;
    } catch (error) {
      console.error('formatDateTime: Date formatting error:', error, 'for value:', dateString);
      return 'Invalid Date';
    }
  };

  const formatUserAgent = (userAgent) => {
    if (!userAgent) return 'Unknown';
    
    // Extract browser and OS info
    const browserMatch = userAgent.match(/(Chrome|Firefox|Safari|Edge|Opera)\/(\d+)/);
    const osMatch = userAgent.match(/(Windows|Mac|Linux|Android|iOS)/);
    
    const browser = browserMatch ? `${browserMatch[1]} ${browserMatch[2]}` : 'Unknown Browser';
    const os = osMatch ? osMatch[1] : 'Unknown OS';
    
    return `${browser} on ${os}`;
  };

  const getCountryFlag = (countryName) => {
    if (!countryName) return '🌍';
    
    // Map of country names to flag emojis
    const countryFlags = {
      'United States': '🇺🇸',
      'United Kingdom': '🇬🇧',
      'Canada': '🇨🇦',
      'Germany': '🇩🇪',
      'France': '🇫🇷',
      'Japan': '🇯🇵',
      'China': '🇨🇳',
      'India': '🇮🇳',
      'Brazil': '🇧🇷',
      'Australia': '🇦🇺',
      'Russia': '🇷🇺',
      'South Korea': '🇰🇷',
      'Italy': '🇮🇹',
      'Spain': '🇪🇸',
      'Netherlands': '🇳🇱',
      'Sweden': '🇸🇪',
      'Norway': '🇳🇴',
      'Denmark': '🇩🇰',
      'Finland': '🇫🇮',
      'Switzerland': '🇨🇭',
      'Austria': '🇦🇹',
      'Belgium': '🇧🇪',
      'Poland': '🇵🇱',
      'Mexico': '🇲🇽',
      'Argentina': '🇦🇷',
      'Chile': '🇨🇱',
      'South Africa': '🇿🇦',
      'Egypt': '🇪🇬',
      'Turkey': '🇹🇷',
      'Israel': '🇮🇱',
      'Saudi Arabia': '🇸🇦',
      'United Arab Emirates': '🇦🇪',
      'Thailand': '🇹🇭',
      'Singapore': '🇸🇬',
      'Malaysia': '🇲🇾',
      'Indonesia': '🇮🇩',
      'Philippines': '🇵🇭',
      'Vietnam': '🇻🇳',
      'New Zealand': '🇳🇿'
    };
    
    return countryFlags[countryName] || '🌍';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">{error}</div>
        <button
          onClick={handleRefresh}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Visitor Analytics</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Track and analyze website visitor data
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-200 dark:border-gray-700 space-y-4">
        {/* Date Range Selector */}
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Date Range:</span>
          </div>
          <div className="flex gap-2">
            {['today', 'yesterday', '7d', '30d', '90d', 'all', 'custom'].map((range) => {
              const getLabel = (range) => {
                switch(range) {
                  case 'today': return 'Today';
                  case 'yesterday': return 'Yesterday';
                  case 'all': return 'All Time';
                  case 'custom': return 'Custom';
                  default: return range.replace('d', ' days');
                }
              };
              
              return (
                <button
                  key={range}
                  onClick={() => setSelectedDateRange(range)}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                    selectedDateRange === range
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {getLabel(range)}
                </button>
              );
            })}
          </div>
          
          {selectedDateRange === 'custom' && (
            <div className="flex gap-2 items-center">
              <input
                type="date"
                value={customDateRange.startDate}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={customDateRange.endDate}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          )}
        </div>
        
        {/* Other Controls */}
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setExcludeBots(!excludeBots)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                excludeBots 
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              {excludeBots ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
              {excludeBots ? 'Humans Only' : 'Include Bots'}
            </button>
            
            <button
              onClick={() => setShowHeatmap(!showHeatmap)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                showHeatmap 
                  ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              <TrendingUp className="h-4 w-4" />
              {showHeatmap ? 'Hide Heatmap' : 'Show Heatmap'}
            </button>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Clear Filters
            </button>
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Enhanced KPI Cards with Fixed Time Periods */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Today Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Today</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {fixedPeriodAnalytics.today?.unique_visitors_today || 0}
              </p>
            </div>
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </motion.div>

        {/* Yesterday Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Yesterday</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {fixedPeriodAnalytics.yesterday?.unique_visitors_range || 0}
              </p>
            </div>
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <Calendar className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </motion.div>

        {/* 7 Days Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">7 Days</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {fixedPeriodAnalytics.sevenDays?.unique_visitors_range || 0}
              </p>
            </div>
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <TrendingUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </motion.div>

        {/* 30 Days Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">30 Days</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {fixedPeriodAnalytics.thirtyDays?.unique_visitors_range || 0}
              </p>
            </div>
            <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <BarChart3 className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
        </motion.div>

        {/* 90 Days Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">90 Days</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {fixedPeriodAnalytics.ninetyDays?.unique_visitors_range || 0}
              </p>
            </div>
            <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
              <Activity className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </motion.div>

        {/* All Time Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">All Time</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {fixedPeriodAnalytics.allTime?.unique_visitors_all_time || 0}
              </p>
            </div>
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
              <Globe className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </motion.div>
      </div>



      {/* First Row - Traffic Sources and Device Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Traffic Sources */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <ExternalLink className="h-5 w-5" />
            Traffic Sources
          </h3>
          <div className="space-y-3">
            {processedData.referrerData.slice(0, 6).map((item, index) => (
              <div key={item.source} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.source}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{item.percentage}%</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{item.count}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Device Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Device Distribution</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={processedData.deviceData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="count"
                >
                  {processedData.deviceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name, props) => [value, props.payload.device]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-4 mt-4">
            {processedData.deviceData.map((item, index) => (
              <div key={item.device} className="flex items-center gap-2">
                {getDeviceIcon(item.device)}
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {item.device} ({item.percentage}%)
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Second Row - Most Visited Dashboard Pages and Path Funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Most Visited Dashboard Pages */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Most Visited Dashboard Pages</h3>
          {analytics && analytics.dashboard_pages && analytics.dashboard_pages.length > 0 ? (
            <div className="space-y-3">
              {analytics.dashboard_pages.map((page, index) => {
                const maxCount = Math.max(...analytics.dashboard_pages.map(p => p.count));
                const width = maxCount > 0 ? (page.count / maxCount) * 100 : 0;
                return (
                  <div key={page.path} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                        {page.path}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{page.count} total visits</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${width}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-500 dark:text-gray-400">
                <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No dashboard page visits found</p>
                <p className="text-sm mt-1">Dashboard pages will appear here once visited</p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Path Funnel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Path Funnel</h3>
          <div className="space-y-4">
            {processedData.funnelData.map((step, index) => {
              const maxCount = Math.max(...processedData.funnelData.map(s => s.count));
              const width = maxCount > 0 ? (step.count / maxCount) * 100 : 0;
              return (
                <div key={step.step} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{step.step}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">{step.count} visits</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${width}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">{step.path}</span>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* Charts Row - Daily Visits and Browser & OS Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Visits Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Daily Unique Visits (Last 30 Days)
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatDate}
                  className="text-xs"
                />
                <YAxis className="text-xs" />
                <Tooltip 
                  labelFormatter={(value) => formatDate(value)}
                  formatter={(value) => [value, 'Unique Visitors']}
                />
                <Line 
                  type="monotone" 
                  dataKey="unique_visitors" 
                  stroke="#3B82F6" 
                  strokeWidth={2}
                  dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Browser & OS Analytics - Compact Version */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700"
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Browser & OS Analytics</h3>
              <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('browsers')}
                  className={`px-3 py-1 rounded-md text-sm transition-colors ${
                    activeTab === 'browsers'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  Browsers
                </button>
                <button
                  onClick={() => setActiveTab('os')}
                  className={`px-3 py-1 rounded-md text-sm transition-colors ${
                    activeTab === 'os'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  OS
                </button>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={activeTab === 'browsers' ? processedData.browserData : processedData.osData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="count"
                    label={({ name, percentage }) => `${name} ${percentage}%`}
                  >
                    {(activeTab === 'browsers' ? processedData.browserData : processedData.osData).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => [value, props.payload.name]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>
      </div>



      {/* Session Heatmap */}
      {showHeatmap && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Session Heatmap</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Peak traffic hours by day of week</p>
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full">
              {/* Hour headers */}
              <div className="flex mb-2">
                <div className="w-12 flex-shrink-0"></div>
                {Array.from({ length: 24 }, (_, i) => (
                  <div key={i} className="w-8 h-6 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
                    {i % 4 === 0 ? i.toString().padStart(2, '0') : ''}
                  </div>
                ))}
              </div>
              {/* Heatmap grid */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, dayIndex) => {
                const maxCount = Math.max(...processedData.heatmapData.map(d => d.count), 1);
                return (
                  <div key={day} className="flex mb-1">
                    <div className="w-12 flex-shrink-0 text-xs text-gray-500 dark:text-gray-400 flex items-center pr-2">
                      {day}
                    </div>
                    {Array.from({ length: 24 }, (_, hour) => {
                      const dataPoint = processedData.heatmapData.find(d => d.dayIndex === dayIndex && d.hour === hour);
                      const count = dataPoint?.count || 0;
                      return (
                        <div
                          key={hour}
                          className={`w-8 h-6 mx-px rounded-sm ${getHeatmapColor(count, maxCount)} cursor-pointer transition-all hover:scale-110 hover:z-10 relative`}
                          title={`${day} ${hour}:00 - ${count} visits`}
                        >
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}

      {/* Map View */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700"
      >
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              Global Visitor Map
              {selectedCountry && (
                <span className="text-sm font-normal text-blue-600 dark:text-blue-400">
                  - Focused on {selectedCountry}
                </span>
              )}
            </h3>
            <div className="flex items-center gap-3">
              {selectedCountry && (
                <button
                  onClick={() => setSelectedCountry(null)}
                  className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  title="Reset map view"
                >
                  Reset View
                </button>
              )}
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Calendar className="h-4 w-4" />
                <span>{getDateRangeDisplayText()}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* World Map Visualization */}
            <div className="lg:col-span-2">
              <div className="relative bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-700 dark:to-gray-600 rounded-lg p-6 min-h-[500px]">
                <div className="text-center mb-6">
                  <Globe className="h-12 w-12 mx-auto mb-3 text-blue-500 dark:text-blue-400" />
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Interactive World Map
                  </h4>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    Visual representation of visitor locations worldwide
                  </p>
                </div>
                
                {/* Interactive World Map */}
                <div className="relative w-full h-96 mb-6">
                  <WorldMap 
                    countriesData={countriesData || []}
                    isDarkMode={isDarkMode}
                    className="w-full h-full"
                    selectedCountry={selectedCountry}
                  />
                </div>
                
                {/* Country cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {(countriesData || []).slice(0, 8).map((country, index) => {
                    const countryName = country.country || country.name;
                const visitCount = country.unique_visitors || country.count || 0;
                    return (
                    <div 
                      key={index} 
                      className={`rounded-lg p-3 shadow-sm transition-all cursor-pointer ${
                        selectedCountry === countryName
                          ? 'bg-blue-100 dark:bg-blue-900 border-2 border-blue-300 dark:border-blue-600 shadow-md'
                          : 'bg-white dark:bg-gray-800 hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => setSelectedCountry(countryName)}
                      title={`Click to view ${countryName} on map`}
                    >
                      <div className="text-xl mb-1">{getCountryFlag(countryName)}</div>
                      <div className="text-xs font-medium text-gray-900 dark:text-white truncate">
                        {countryName}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {visitCount} visits
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            </div>
            
            {/* Country Statistics */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="font-medium text-gray-900 dark:text-white">
                  Top Countries ({(countriesData || []).length})
                </h4>
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <Calendar className="h-3 w-3" />
                  <span>{getDateRangeDisplayText()}</span>
                </div>
              </div>
              <div className="space-y-3">
                {(countriesData || []).map((country, index) => {
                  const countryName = country.country || country.name;
              const visitCount = country.unique_visitors || country.count || 0;
                  const totalVisits = (countriesData || []).reduce((sum, c) => sum + (c.unique_visitors || c.count || 0), 0);
                  const percentage = totalVisits > 0 ? (visitCount / totalVisits * 100) : 0;
                  return (
                    <div 
                       key={index} 
                       className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                         selectedCountry === countryName 
                           ? 'bg-blue-100 dark:bg-blue-900 border-2 border-blue-300 dark:border-blue-600' 
                           : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'
                       }`}
                       onClick={() => setSelectedCountry(countryName)}
                       title={`Click to view ${countryName} on map`}
                     >
                      <div className="flex items-center gap-3">
                        <span className="text-lg">{getCountryFlag(countryName)}</span>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white text-sm">
                            {countryName}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {visitCount} visits
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {percentage.toFixed(1)}%
                        </div>
                        <div className="w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-2 mt-1">
                          <div 
                            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Enhanced Recent Visits Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700"
      >
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Recent Visits ({(processedData.filteredVisits || []).length})
            </h3>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm text-gray-500 dark:text-gray-400">Filters:</span>
            </div>
          </div>
          
          {/* Filter Inputs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <input
              type="text"
              placeholder="Filter by country..."
              value={filters.country}
              onChange={(e) => updateFilter('country', e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="text"
              placeholder="Filter by path..."
              value={filters.path}
              onChange={(e) => updateFilter('path', e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="text"
              placeholder="Filter by referrer..."
              value={filters.referrer}
              onChange={(e) => updateFilter('referrer', e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <select
              value={filters.device}
              onChange={(e) => updateFilter('device', e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All devices</option>
              <option value="mobile">Mobile</option>
              <option value="desktop">Desktop</option>
              <option value="tablet">Tablet</option>
            </select>
          </div>
        </div>
        
        <div className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-full table-fixed">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="w-1/4 px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    <div className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      <span className="hidden sm:inline">Page</span>
                    </div>
                  </th>
                  <th className="w-1/6 px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      <span className="hidden sm:inline">Country</span>
                    </div>
                  </th>
                  <th className="w-1/5 px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    <div className="flex items-center gap-1">
                      <Monitor className="h-3 w-3" />
                      <span className="hidden md:inline">Browser</span>
                    </div>
                  </th>
                  <th className="w-1/5 px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    <div className="flex items-center gap-1">
                      <ExternalLink className="h-3 w-3" />
                      <span className="hidden md:inline">Referrer</span>
                    </div>
                  </th>
                  <th className="w-1/8 px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span className="hidden lg:inline">Time</span>
                    </div>
                  </th>
                  <th className="w-12 px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    <div className="flex items-center gap-1">
                      <Bot className="h-3 w-3" />
                      <span className="hidden lg:inline">Type</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {(processedData.filteredVisits || []).slice(0, 50).map((visit, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-3 py-3 text-xs text-gray-900 dark:text-white">
                      <div className="truncate" title={visit.path || '/'}>
                        <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">
                          {(() => {
                            const path = visit.path || '/';
                            if (path === '/') return '/';
                            const segments = path.split('/');
                            const lastSegment = segments[segments.length - 1] || segments[segments.length - 2];
                            return lastSegment && lastSegment.length > 0 ? `.../${lastSegment}` : path;
                          })()}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-900 dark:text-white">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{getCountryFlag(visit.country)}</span>
                        <div className="truncate">
                          <div className="truncate" title={visit.country || 'Unknown'}>
                            {visit.country || 'Unknown'}
                          </div>
                          {visit.city && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate hidden sm:block" title={visit.city}>
                              {visit.city}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-900 dark:text-white">
                      <div className="break-words" title={formatUserAgent(visit.user_agent)}>
                        {formatUserAgent(visit.user_agent)}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-900 dark:text-white">
                      <div className="truncate" title={visit.referrer || 'Direct'}>
                        <span className="text-blue-600 dark:text-blue-400">
                          {(() => {
                            if (!visit.referrer) return 'Direct';
                            
                            // For URLs with protocols, extract hostname
                            if (visit.referrer.includes('://')) {
                              try {
                                const url = new URL(visit.referrer);
                                return url.hostname;
                              } catch {
                                // If URL parsing fails, show last part after ://
                                const parts = visit.referrer.split('://');
                                if (parts.length > 1) {
                                  const afterProtocol = parts[1];
                                  const segments = afterProtocol.split('/');
                                  return segments.length > 1 ? `.../${segments[segments.length - 1]}` : afterProtocol;
                                }
                                return visit.referrer;
                              }
                            }
                            
                            // For other referrers, show as is
                            return visit.referrer;
                          })()}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-500 dark:text-gray-400">
                      <div className="truncate" title={formatDateTime(visit.timestamp)}>
                        {new Date(visit.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        <div className="text-xs opacity-75 hidden lg:block">
                          {new Date(visit.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
                        visit.is_bot 
                          ? 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200'
                          : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                      }`} title={visit.is_bot ? 'Bot' : 'Human'}>
                        {visit.is_bot ? <Bot className="h-3 w-3" /> : <User className="h-3 w-3" />}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          </div>
          {(processedData.filteredVisits || []).length === 0 && (
             <div className="text-center py-12 text-gray-500 dark:text-gray-400">
               <Filter className="h-12 w-12 mx-auto mb-4 opacity-50" />
               <p>No visits match your current filters</p>
               <button
                 onClick={clearFilters}
                 className="mt-2 text-blue-600 dark:text-blue-400 hover:underline"
               >
                 Clear all filters
               </button>
             </div>
           )}
           {(processedData.filteredVisits || []).length > 50 && (
             <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
               Showing first 50 of {(processedData.filteredVisits || []).length} visits
             </div>
           )}
        </div>
      </motion.div>
    </div>
  );
};

export default VisitorAnalytics;